import { useEffect, useState } from "react";
import { Loader2, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ApiError, formatApiError } from "@/lib/api-error";
import {
  axisValueLabel,
  fonteLabel,
  getDisponibilidadeConfig,
  EIXO_LABEL,
  TEMPERATURA_CONFIG,
  type DisponibilidadeSummary,
} from "@/utils/disponibilidadeUtils";
import { formatLocalDate, formatDateOnly } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";
import { useDisponibilidade, useReconsulta, type Reconsulta } from "@/hooks/useDisponibilidade";
import { HistoricoDisponibilidadeContent } from "./HistoricoDisponibilidadeContent";
import {
  useAtualizarDisponibilidade,
  type InteresseValue,
  type EmpregoValue,
} from "@/hooks/useAtualizarDisponibilidade";

const API_BASE_URL = (
  (import.meta.env.VITE_SUPABASE_URL as string) || "https://api.coriscofrila.com.br/"
).replace(/\/$/, "");

const INTERESSE_OPTIONS: InteresseValue[] = ["topa_freela", "aberto_mas_ocupado", "nao_topa"];
const EMPREGO_OPTIONS: EmpregoValue[] = ["empregado_fixo", "freela_ativo", "entre_trabalhos"];

interface ExtractedAxis {
  value: string;
  confidence: "alta" | "media" | "baixa";
  quote: string;
  abstained: boolean;
}
interface ExtractionResult {
  dedup_key: string;
  already_processed: boolean;
  extracted: {
    resumo?: string;
    interesse_freela?: ExtractedAxis;
    situacao_emprego?: ExtractedAxis;
  };
}

interface Props {
  freelancerId: string;
  freelancerNome: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Chamado após um salvamento bem-sucedido (manual ou WhatsApp). Ex.: resolver uma reconsulta. */
  onSaved?: () => void;
  /** Pré-preenche a aba manual ao abrir (ex.: hipótese do resumo do WhatsApp); a pessoa confirma. */
  inicial?: {
    interesse?: InteresseValue | null;
    emprego?: EmpregoValue | null;
    observacao?: string;
  };
  /** De onde veio a sugestão; gravado no sinal para rastrear a origem. */
  sourceRef?: Record<string, unknown>;
}

function OptionButtons<T extends string>({
  options,
  selected,
  dimension,
  onSelect,
}: {
  options: T[];
  selected: T | null;
  dimension: string;
  onSelect: (value: T | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => (
        <Button
          key={option}
          type="button"
          size="sm"
          variant={selected === option ? "default" : "outline"}
          onClick={() => onSelect(selected === option ? null : option)}
        >
          {axisValueLabel(dimension, option)}
        </Button>
      ))}
    </div>
  );
}

/**
 * Último status salvo, só leitura. Os campos do formulário NÃO são pré-preenchidos com
 * isso de propósito: registrar é afirmar um fato novo, e pré-preencher faria salvar um
 * sinal repetido com a data de hoje sem intenção.
 */
function UltimoRegistro({
  summary,
  reconsulta,
  isLoading,
}: {
  summary: DisponibilidadeSummary | null | undefined;
  reconsulta: Reconsulta | null | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
        Carregando último registro...
      </div>
    );
  }
  if (!summary) {
    return (
      <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
        Nenhum registro de disponibilidade ainda.
      </div>
    );
  }

  const config = getDisponibilidadeConfig(summary.disponibilidade_efetiva);
  const StatusIcon = config.icon;
  const axes = summary.axes ?? {};
  const interesseAxis = axes["interesse_freela"];
  const empregoAxis = axes["situacao_emprego"];
  // Só interesse/emprego: `ultimo_sinal` inclui relação, que os triggers de projeto
  // mexem sozinhos e daria uma data que ninguém registrou. Mais recente primeiro.
  const registrados = [interesseAxis, empregoAxis]
    .filter((axis): axis is NonNullable<typeof axis> => !!axis)
    .sort((primeiro, segundo) => (segundo.observed_at ?? "").localeCompare(primeiro.observed_at ?? ""));
  const registradoEm = registrados[0]?.observed_at ?? summary.ultimo_sinal ?? null;
  // Interesse e emprego salvos juntos compartilham a observação: não repetir na tela.
  const observacoes = Array.from(
    new Set(
      registrados
        .map((axis) => axis.observation?.trim())
        .filter((observation): observation is string => !!observation)
    )
  );
  // Relação só aparece quando existe: ela vem dos triggers de projeto, não de um registro
  // manual, e uma linha "Sem info" fixa daria a impressão de campo que se preenche aqui.
  const eixosExibidos = [
    "interesse_freela",
    "situacao_emprego",
    ...(axes["relacao_conosco"] ? ["relacao_conosco"] : []),
  ];
  const temperatura = summary.temperatura && summary.temperatura !== "sem_info"
    ? TEMPERATURA_CONFIG[summary.temperatura]
    : null;

  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Último registro</span>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium",
            config.bg,
            config.border,
            config.color
          )}
        >
          <StatusIcon className="h-3.5 w-3.5 shrink-0" />
          {config.text}
        </span>
        {temperatura && (
          <span
            className={cn(
              "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
              temperatura.bg,
              temperatura.color,
              temperatura.border
            )}
          >
            {temperatura.text}
          </span>
        )}
      </div>

      <div className="text-xs text-muted-foreground">
        {registradoEm ? `Registrado em ${formatDateOnly(registradoEm)}` : "Sem data de registro"}
        {reconsulta?.due_at && ` · Reconsultar em ${formatDateOnly(reconsulta.due_at)}`}
      </div>

      <dl className="space-y-0.5 text-xs">
        {eixosExibidos.map((dimension) => {
          const fonte = fonteLabel(axes[dimension]?.source);
          return (
            <div key={dimension} className="flex flex-wrap gap-1">
              <dt className="text-muted-foreground">{EIXO_LABEL[dimension]}:</dt>
              <dd className="font-medium">{axisValueLabel(dimension, axes[dimension]?.value)}</dd>
              {fonte && <dd className="text-muted-foreground">· {fonte}</dd>}
            </div>
          );
        })}
      </dl>

      {observacoes.map((observation) => (
        <p key={observation} className="text-xs text-muted-foreground whitespace-pre-wrap wrap-break-word">
          {observation}
        </p>
      ))}
    </div>
  );
}

export function AtualizarDisponibilidadeDialog({
  freelancerId,
  freelancerNome,
  open,
  onOpenChange,
  inicial,
  sourceRef,
  onSaved,
}: Props) {
  const { toast } = useToast();
  const atualizar = useAtualizarDisponibilidade(freelancerId);
  const { data: summary, isLoading: summaryLoading } = useDisponibilidade(open ? freelancerId : null);
  const { data: reconsulta } = useReconsulta(open ? freelancerId : null);

  const hoje = formatLocalDate(new Date()) ?? "";

  const [tab, setTab] = useState("manual");

  // aba manual
  const [interesse, setInteresse] = useState<InteresseValue | null>(null);
  const [emprego, setEmprego] = useState<EmpregoValue | null>(null);
  const [observacao, setObservacao] = useState("");
  const [observadoEm, setObservadoEm] = useState(hoje);
  const [reconsultarEm, setReconsultarEm] = useState("");

  // aba whatsapp
  const [whatsappText, setWhatsappText] = useState("");
  const [extraindo, setExtraindo] = useState(false);
  const [extraction, setExtraction] = useState<ExtractionResult | null>(null);
  const [observacaoWhatsapp, setObservacaoWhatsapp] = useState("");

  useEffect(() => {
    if (!open || !inicial) return;
    setTab("manual");
    setInteresse(inicial.interesse ?? null);
    setEmprego(inicial.emprego ?? null);
    setObservacao(inicial.observacao ?? "");
    // Só ao abrir: depois disso o formulário é da pessoa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Resumo da IA entra como rascunho editável da observação a cada nova extração.
  useEffect(() => {
    if (extraction) setObservacaoWhatsapp(extraction.extracted.resumo ?? "");
  }, [extraction]);

  function resetAndClose() {
    setTab("manual");
    setInteresse(null);
    setEmprego(null);
    setObservacao("");
    setObservadoEm(hoje);
    setReconsultarEm("");
    setWhatsappText("");
    setExtraction(null);
    setObservacaoWhatsapp("");
    onOpenChange(false);
  }

  async function salvarManual() {
    if (!interesse && !emprego) {
      toast({ variant: "destructive", title: "Escolha ao menos o interesse ou a situação de emprego." });
      return;
    }
    try {
      await atualizar.mutateAsync({
        interesse: interesse ?? undefined,
        emprego: emprego ?? undefined,
        observacao,
        // Só manda a data da observação se o usuário mudou de hoje (senão o hook usa now()).
        observadoEm: observadoEm && observadoEm !== hoje ? observadoEm : undefined,
        reconsultarEm: reconsultarEm || undefined,
        sourceRef,
      });
      toast({ title: "Disponibilidade atualizada" });
      onSaved?.();
      resetAndClose();
    } catch (error) {
      toast({ variant: "destructive", title: "Erro ao salvar", description: formatApiError(error).detail });
    }
  }

  async function extrair() {
    setExtraindo(true);
    setExtraction(null);
    try {
      const result = await apiExtrair(freelancerId, freelancerNome, whatsappText);
      setExtraction(result);
    } catch (error) {
      toast({ variant: "destructive", title: "Falha na extração", description: formatApiError(error).detail });
    } finally {
      setExtraindo(false);
    }
  }

  async function aplicarExtracao() {
    if (!extraction) return;
    const interesseAxis = extraction.extracted.interesse_freela;
    const empregoAxis = extraction.extracted.situacao_emprego;
    const interesseValue =
      interesseAxis && !interesseAxis.abstained && interesseAxis.value !== "desconhecido"
        ? (interesseAxis.value as InteresseValue)
        : undefined;
    const empregoValue =
      empregoAxis && !empregoAxis.abstained && empregoAxis.value !== "desconhecido"
        ? (empregoAxis.value as EmpregoValue)
        : undefined;
    if (!interesseValue && !empregoValue) {
      toast({ variant: "destructive", title: "A IA não extraiu nenhum sinal aplicável." });
      return;
    }
    try {
      await atualizar.mutateAsync({
        interesse: interesseValue,
        emprego: empregoValue,
        observacao: observacaoWhatsapp,
        whatsappText,
        authorKind: "ia",
        dedupKey: extraction.dedup_key,
      });
      toast({ title: "Sinais do WhatsApp aplicados" });
      onSaved?.();
      resetAndClose();
    } catch (error) {
      toast({ variant: "destructive", title: "Erro ao aplicar", description: formatApiError(error).detail });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Atualizar disponibilidade</DialogTitle>
          <DialogDescription>{freelancerNome}</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="manual">Registrar</TabsTrigger>
            <TabsTrigger value="whatsapp">Do WhatsApp</TabsTrigger>
            <TabsTrigger value="historico">Histórico</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="space-y-4 pt-2">
            <UltimoRegistro
              summary={summary}
              reconsulta={reconsulta}
              isLoading={summaryLoading}
            />
            <div className="space-y-1.5">
              <Label>Interesse em freela</Label>
              <OptionButtons<InteresseValue>
                options={INTERESSE_OPTIONS}
                selected={interesse}
                dimension="interesse_freela"
                onSelect={setInteresse}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground">Situação de emprego (opcional)</Label>
              <OptionButtons<EmpregoValue>
                options={EMPREGO_OPTIONS}
                selected={emprego}
                dimension="situacao_emprego"
                onSelect={setEmprego}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Observação</Label>
              <Textarea
                value={observacao}
                onChange={(event) => setObservacao(event.target.value)}
                placeholder="O que ficou combinado, contexto, etc."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-muted-foreground">Quando foi isso?</Label>
                <Input
                  type="date"
                  value={observadoEm}
                  max={hoje}
                  onChange={(event) => setObservadoEm(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-muted-foreground">Reconsultar em (opcional)</Label>
                <Input
                  type="date"
                  value={reconsultarEm}
                  min={hoje}
                  onChange={(event) => setReconsultarEm(event.target.value)}
                  placeholder="Quando pode estar livre"
                />
              </div>
            </div>
            <Button className="w-full" onClick={salvarManual} disabled={atualizar.isPending}>
              {atualizar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </TabsContent>

          <TabsContent value="whatsapp" className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label>Cole a conversa do WhatsApp</Label>
              <Textarea
                value={whatsappText}
                onChange={(event) => setWhatsappText(event.target.value)}
                placeholder="Cole aqui a conversa. A IA lê e sugere os sinais para você revisar e aplicar."
                rows={6}
              />
            </div>
            <Button
              variant="secondary"
              className="w-full"
              onClick={extrair}
              disabled={extraindo || whatsappText.trim().length < 10}
            >
              {extraindo ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Extrair com IA
            </Button>

            {extraction && (
              <div className="space-y-2 rounded-lg border p-3">
                {extraction.already_processed && (
                  <div className="flex items-start gap-2 text-xs text-amber-600">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    Essa conversa já foi processada antes. Aplicar de novo não duplica.
                  </div>
                )}
                <ExtractedAxisRow
                  label="Interesse em freela"
                  dimension="interesse_freela"
                  axis={extraction.extracted.interesse_freela}
                />
                <ExtractedAxisRow
                  label="Situação de emprego"
                  dimension="situacao_emprego"
                  axis={extraction.extracted.situacao_emprego}
                />
                <div className="space-y-1.5">
                  <Label className="text-xs">Observação</Label>
                  <Textarea
                    value={observacaoWhatsapp}
                    onChange={(event) => setObservacaoWhatsapp(event.target.value)}
                    placeholder="Resumo do que ficou combinado. A conversa completa fica salva no histórico."
                    rows={3}
                  />
                </div>
                <Button className="w-full" onClick={aplicarExtracao} disabled={atualizar.isPending}>
                  {atualizar.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                  )}
                  Aplicar sinais aprovados
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="historico" className="pt-4">
            <HistoricoDisponibilidadeContent
              freelancerId={freelancerId}
              enabled={open && tab === "historico"}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function ExtractedAxisRow({
  label,
  dimension,
  axis,
}: {
  label: string;
  dimension: string;
  axis?: ExtractedAxis;
}) {
  if (!axis || axis.abstained || axis.value === "desconhecido") {
    return (
      <div className="text-xs text-muted-foreground">
        {label}: <span className="italic">não mencionado</span>
      </div>
    );
  }
  return (
    <div className="text-xs">
      <span className="font-medium">{label}:</span> {axisValueLabel(dimension, axis.value)}{" "}
      <span className="text-muted-foreground">(confiança {axis.confidence})</span>
      {axis.quote && (
        <p className="text-muted-foreground italic mt-0.5">"{axis.quote}"</p>
      )}
    </div>
  );
}

async function apiExtrair(
  freelancerId: string,
  freelancerNome: string,
  texto: string,
): Promise<ExtractionResult> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const response = await fetch(`${API_BASE_URL}/agents/disponibilidade/extrair-whatsapp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      freelancer_id: freelancerId,
      freelancer_nome: freelancerNome,
      texto,
    }),
  });
  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      url: response.url,
      method: "POST",
      body: await response.text().catch(() => ""),
    });
  }
  return response.json();
}

export default AtualizarDisponibilidadeDialog;
